import { Trans, useI18n } from '@affine/i18n';
import { BRAND_DISPLAY_NAME } from '@streamlain/brand';
import clsx from 'clsx';
import { type ReactNode, useCallback } from 'react';

import { BrandLogo, BrandWordmark, STREAMLAIN_BRAND_LOCKUP } from '../../brand';
import { Button } from '../../ui/button';
import * as styles from './index.css';

const LoadingContactMailLink = ({ children }: { children?: ReactNode }) => (
  <>
    <br />
    <a
      className={styles.contactMailLink}
      href="mailto:support@streamlain.de"
      target="__blank"
    >
      {children}
    </a>
  </>
);

export const LoadingScreenLogo = ({
  animated = false,
}: {
  animated?: boolean;
}) => {
  return (
    <div className={styles.loadingBrand}>
      <BrandLogo
        size={160}
        animate={animated}
        className={styles.loadingLogo}
        aria-label={BRAND_DISPLAY_NAME}
      />
      <BrandWordmark
        size={STREAMLAIN_BRAND_LOCKUP.wordmarkWidth}
        className={styles.loadingWordmark}
        aria-label={`${BRAND_DISPLAY_NAME} wordmark`}
      />
    </div>
  );
};

export const LoadingSplashForeground = ({ label }: { label?: string }) => {
  return (
    <div
      className={styles.loadingSplashForeground}
      role={label ? 'status' : undefined}
      aria-label={label}
      data-testid="loading-splash-foreground"
    >
      <LoadingScreenLogo animated />
    </div>
  );
};

/**
 * Shared brand screen: the StreamLain logo lockup on the theme-bound surface,
 * with a slot for the bottom block. Reused by the doc loading state and the
 * not-found/no-access pages so transitions between them cause no layout shift.
 */
export const BrandScreen = ({
  animated = false,
  children,
}: {
  animated?: boolean;
  children?: ReactNode;
}) => {
  return (
    <div className={styles.blockSuiteEditorStyle}>
      <div className={styles.brandAnchor}>
        <LoadingScreenLogo animated={animated} />
        <div className={styles.brandSlot}>{children}</div>
      </div>
    </div>
  );
};

export const EditorLoading = ({
  longerLoading = false,
}: {
  longerLoading?: boolean;
}) => {
  const t = useI18n();
  const reloadPage = useCallback(() => {
    document.location.reload();
  }, []);
  return (
    <BrandScreen animated={!longerLoading}>
      {longerLoading ? (
        <div className={styles.content} data-longer-loading={true}>
          <div>
            <div className={styles.text} data-longer-loading={true}>
              {t['com.streamlain.error.loading-timeout-error']()}
            </div>
            <div className={styles.text} data-longer-loading={true}>
              <Trans
                i18nKey="com.streamlain.error.contact-us"
                components={{
                  1: <LoadingContactMailLink />,
                }}
              />
            </div>
          </div>
          <Button
            size="large"
            className={clsx(
              BUILD_CONFIG.isMobileEdition
                ? styles.mobileActionButton
                : styles.actionButton
            )}
            contentClassName={clsx(
              BUILD_CONFIG.isMobileEdition
                ? styles.mobileActionContent
                : styles.actionContent
            )}
            onClick={reloadPage}
            variant="primary"
          >
            {t['com.streamlain.error.reload']()}
          </Button>
        </div>
      ) : (
        <div className={styles.content}>
          <div className={styles.title}>{t['com.streamlain.loading']()}</div>
          <div className={styles.text}>
            {t['com.streamlain.loading.description']()}
          </div>
        </div>
      )}
    </BrandScreen>
  );
};

export const PageDetailLoading = () => {
  return (
    <div className={styles.pageDetailSkeletonStyle}>
      <EditorLoading />
    </div>
  );
};
